// ForgotPasswordPage.tsx
import React, { useState, useEffect } from 'react';
import { useNotify, required, SimpleForm, TextInput, useDataProvider } from 'react-admin';
import { Typography, TextField, Box, Stack, CircularProgress } from '@mui/material';
import AuthHelper from '../../utils/AuthHelper';
import { useNavigate, useParams } from 'react-router-dom';
import RegisterLayout from '../RegisterLayout';
import RegisterLayoutWithoutHeader from '../RegisterLayoutWithoutHeader';
import { ActionButton } from '../ActionButton';
import PasswordInputWithToggle from '../PasswordInputWithToggle';
import RegisterSubmitButton from '../RegisterSubmitButton';

interface ForgotPasswordPageProps {
  backgroundColor?: string;
  buttonColor?: string;
}

interface PartnerLoginInfo {
  loginId: string;
  clientId: number;
  name: string;
  companyName: string;
  storeLogoThumbnail: {
    src: string;
  };
  userPoolId: string;
  cognitoClientId: string;
}

const CustomToolbar = ({ loading }: { loading: boolean }) => (
  <Box display="flex" justifyContent="center" mt={5}>
    <RegisterSubmitButton type="submit" disabled={loading}>
      パスワードをリセット
    </RegisterSubmitButton>
  </Box>
);

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({ buttonColor = '#BA7967' }) => {
  const [username, setUsername] = useState('');
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [partnerInfo, setPartnerInfo] = useState<PartnerLoginInfo | null>(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [infoError, setInfoError] = useState(false);

  const navigate = useNavigate();
  const notify = useNotify();
  const { tenantId } = useParams<{ tenantId?: string }>();
  const dataProvider = useDataProvider();

  // テナントIDに基づいてパートナー情報を取得
  useEffect(() => {
    const fetchPartnerInfo = async () => {
      if (!tenantId) {
        setInfoLoading(false);
        return;
      }

      try {
        const response = await dataProvider.getPartnerLoginInfo(tenantId);
        setPartnerInfo(response);

        // Cognito設定をlocalStorageに保存
        localStorage.setItem('cognitoUserPoolId', response.userPoolId);
        localStorage.setItem('cognitoClientId', response.cognitoClientId);
      } catch (err) {
        console.error('Failed to fetch partner info:', err);
        setInfoError(true);
      } finally {
        setInfoLoading(false);
      }
    };

    fetchPartnerInfo();
  }, [tenantId, dataProvider]);

  const handleSendCode = async (event: any) => {
    event.preventDefault();

    try {
      // 動的なCognito設定を取得
      if (tenantId) {
        const userPoolId = localStorage.getItem('cognitoUserPoolId') ?? import.meta.env.VITE_PARTNER_USER_POOL_ID;
        const clientId = localStorage.getItem('cognitoClientId') ?? import.meta.env.VITE_PARTNER_USER_POOL_CLIENT_ID;

        // AuthHelperを動的に初期化
        AuthHelper.init(import.meta.env.VITE_COGNITO_ENDPOINT, userPoolId, clientId);
      }

      const result = await AuthHelper.forgotPassword(username);
      if (result.isSendCode) {
        setStep(2);
      } else {
        navigate(tenantId ? `/reset-complete/${tenantId}` : '/reset-complete');
      }
    } catch (err) {
      notify(AuthHelper.getErrorKeyCode(err), { type: 'error' });
    }
  };

  const handleResetPassword = async (data: any) => {
    try {
      if (tenantId) {
        // 動的なCognito設定を取得
        const userPoolId = localStorage.getItem('cognitoUserPoolId') ?? import.meta.env.VITE_PARTNER_USER_POOL_ID;
        const clientId = localStorage.getItem('cognitoClientId') ?? import.meta.env.VITE_PARTNER_USER_POOL_CLIENT_ID;

        // AuthHelperを動的に初期化
        AuthHelper.init(import.meta.env.VITE_COGNITO_ENDPOINT, userPoolId, clientId);
      }

      const code = data.code;
      const newPassword = data.newPassword;
      await AuthHelper.setNewPassword(username, code, newPassword);
      navigate(tenantId ? `/reset-complete/${tenantId}` : '/reset-complete');
    } catch (err) {
      const errorCode = (err as { code?: string }).code;
      if (errorCode === 'InvalidParameterException') {
        (err as { code?: string }).code = 'InvalidPasswordException';
      }
      notify(AuthHelper.getErrorKeyCode(err), { type: 'error' });
      return false;
    }
  };

  // パートナー情報のローディング中
  if (infoLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // パートナー情報に基づいてロゴを設定
  const storeLogoThumbnail = partnerInfo?.storeLogoThumbnail?.src;
  const partnerName = partnerInfo?.name;

  return (
    <RegisterLayoutWithoutHeader
      storeLogoThumbnail={storeLogoThumbnail}
      inviteSite={partnerName}
    >
      <Box display="flex" alignItems="center" justifyContent="center">
        {step === 1 ? (
          <Stack
            component="form"
            onSubmit={handleSendCode}
            noValidate
            sx={{ maxWidth: '600px', width: '100%' }}
            spacing={5}
            justifyContent={'center'}
            alignItems={'center'}
            direction={'column'}
          >
            <Typography variant="h5" gutterBottom align="center">
              パスワードリセット
            </Typography>

            <TextField
              label="メールアドレス"
              margin="normal"
              value={username}
              fullWidth
              onChange={(e) => setUsername(e.target.value)}
              required
            />

            <ActionButton
              variant="contained"
              type="submit"
              disabled={!username} // メールアドレスが入力されていないと無効化
              sx={{
                backgroundColor: !username ? 'lightgray' : buttonColor // 無効時の背景色を変更
              }}
            >
              コードを送信
            </ActionButton>
          </Stack>
        ) : (
          <Stack sx={{ maxWidth: '600px', width: '100%' }} spacing={5}>
            <Typography variant="h5" gutterBottom align="center">
              パスワードリセット
            </Typography>

            <SimpleForm
              toolbar={<CustomToolbar loading={isLoading} />}
              sx={{ maxWidth: '600px', width: '100%' }}
              onSubmit={handleResetPassword}
              gap={3}
            >
              <TextInput source="code" label="認証コード" fullWidth autoComplete="off" validate={required()} />

              <PasswordInputWithToggle
                source="newPassword"
                label={'新しいパスワード'}
                validate={required()}
                helperText="パスワードは8文字以上で、数字、記号、大文字、小文字をそれぞれ1つ以上含める必要があります。"
                FormHelperTextProps={{ sx: { whiteSpace: 'normal' } }}
              />
            </SimpleForm>
          </Stack>
        )}
      </Box>
    </RegisterLayoutWithoutHeader>
  );
};

export default ForgotPasswordPage;
